// SPDX-License-Identifier: AGPL-3.0-or-later

import type {FeedUploadRefusal} from '@app/features/feed/commands/FeedCommands';
import * as FeedVideoCommands from '@app/features/feed/commands/FeedVideoCommands';
import {toastFeedFailure} from '@app/features/feed/components/FeedItemMenu';
import styles from '@app/features/feed/components/FeedVideoPosterPicker.module.css';
import {FEED_IMAGE_TYPES, FEED_MAX_IMAGE_BYTES, FEED_POSTER_ACCEPT} from '@app/features/feed/utils/FeedUtils';
import {posterFromFrame} from '@app/features/feed/utils/FeedVideoPoster';
import {openFilePicker} from '@app/features/messaging/utils/FilePickerUtils';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {FilmStripIcon, ImageIcon, XIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect, useMemo, useRef, useState} from 'react';

const COVER_DESCRIPTOR = msg({
	message: 'Cover',
	comment: 'Title of the part of the new video post where you choose the image shown before the video plays.',
});
const COVER_AUTO_DESCRIPTOR = msg({
	message: 'Until you choose one, a frame from the video is shown.',
	comment: 'Hint under the video post cover title while no cover is chosen.',
});
const COVER_CHOSEN_DESCRIPTOR = msg({
	message: 'This image is shown before the video plays.',
	comment: 'Hint under the video post cover title once a cover image is chosen.',
});
const COVER_NO_FRAMES_DESCRIPTOR = msg({
	message: "This browser can't show this video's frames, so upload an image for the cover.",
	comment: 'Hint when the browser cannot decode the picked video, so a frame cannot be picked as its cover.',
});
const UPLOAD_COVER_DESCRIPTOR = msg({
	message: 'Upload image',
	comment: 'Button that picks an image file as the cover of the new video post.',
});
const PICK_FRAME_DESCRIPTOR = msg({
	message: 'Pick a frame',
	comment: 'Button that opens a slider to choose one frame of the picked video as its cover.',
});
const REMOVE_COVER_DESCRIPTOR = msg({
	message: 'Remove cover',
	comment: 'Accessible label of the button that takes the chosen cover off the new video post.',
});
const USE_FRAME_DESCRIPTOR = msg({
	message: 'Use this frame',
	comment: 'Button that makes the frame shown above the slider the cover of the new video post.',
});
const CANCEL_FRAME_DESCRIPTOR = msg({
	message: 'Cancel',
	comment: 'Button that closes the frame slider without changing the cover.',
});
const FRAME_TIME_DESCRIPTOR = msg({
	message: 'Frame time',
	comment: 'Accessible label of the slider that moves through the video to choose the cover frame.',
});
const FRAME_FAILED_DESCRIPTOR = msg({
	message: "This frame couldn't be saved. Pick another one or upload an image.",
	comment: 'Toast when the chosen video frame cannot be turned into a cover image in this browser.',
});
const COVER_TYPE_REJECTED_DESCRIPTOR = msg({
	message: 'A cover must be a PNG, JPEG or WebP image of {maxSize} or smaller.',
	comment:
		'Toast when the picked cover file is not a still image or is too large. {maxSize} is the limit, e.g. "50 MB".',
});

const UPLOAD_LIMIT_DESCRIPTOR = msg({
	message: 'This file is over your upload limit of {maxSize}.',
	comment:
		'Toast when the server refuses a feed image, cover or video as larger than the limit of this account. {maxSize} is that limit, for example "500 MB". Nothing was uploaded.',
});
const COVER_CHANGED_DESCRIPTOR = msg({
	message: 'Cover changed.',
	comment: 'Toast after the author replaced the cover image of their video post.',
});

// The server's refusal with the account's own limit: its size, or the length message it wrote.
export function feedUploadRefusalText(i18n: I18n, refusal: FeedUploadRefusal): string {
	return refusal.kind === 'too_long'
		? refusal.message
		: i18n._(UPLOAD_LIMIT_DESCRIPTOR, {maxSize: formatFileSize(i18n.locale, refusal.maxBytes)});
}

// A still PNG, JPEG or WebP within the hard cap: null when the file fits, else the toast that says why not.
function coverRejectionText(i18n: I18n, file: File): string | null {
	if (FEED_IMAGE_TYPES.has(file.type) && file.type !== 'image/gif' && file.size <= FEED_MAX_IMAGE_BYTES) return null;
	return i18n._(COVER_TYPE_REJECTED_DESCRIPTOR, {maxSize: formatFileSize(i18n.locale, FEED_MAX_IMAGE_BYTES)});
}

// The post menu's "Change cover" on your own video post: an image file only (the post's video is not decoded here).
export async function changeVideoPoster(i18n: I18n, postId: string): Promise<void> {
	const [file] = await openFilePicker({accept: FEED_POSTER_ACCEPT});
	if (!file) return;
	const rejection = coverRejectionText(i18n, file);
	if (rejection) {
		ToastCommands.error(rejection);
		return;
	}
	const outcome = await FeedVideoCommands.setVideoPoster(postId, file);
	if (outcome === 'ok') ToastCommands.success(i18n._(COVER_CHANGED_DESCRIPTOR));
	else if (typeof outcome === 'object') ToastCommands.error(feedUploadRefusalText(i18n, outcome));
	else toastFeedFailure(i18n, outcome);
}

// m:ss, or h:mm:ss from an hour on.
function clock(seconds: number): string {
	const whole = Math.max(0, Math.floor(seconds));
	const parts = [Math.floor(whole / 3600), Math.floor((whole % 3600) / 60), whole % 60];
	const [hours, minutes, rest] = parts as [number, number, number];
	const pad = (value: number) => String(value).padStart(2, '0');
	return hours > 0 ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${minutes}:${pad(rest)}`;
}

interface FeedVideoPosterPickerProps {
	// The picked video's object URL (the composer's preview).
	videoUrl: string;
	// Whether the browser shows the video's frames; without them only an image can be the cover.
	decodable: boolean;
	duration: number | null;
	poster: File | null;
	onChange: (poster: File | null) => void;
	disabled: boolean;
}

// Micup akis-v3: the cover of a new video post, an uploaded image or a frame of the video drawn in the browser (JPEG).
// Without one the post shows Bunny's own thumbnail.
export const FeedVideoPosterPicker = observer(
	({videoUrl, decodable, duration, poster, onChange, disabled}: FeedVideoPosterPickerProps) => {
		const {i18n} = useLingui();
		const [choosing, setChoosing] = useState(false);
		const [time, setTime] = useState(0);
		const [saving, setSaving] = useState(false);
		const frameRef = useRef<HTMLVideoElement>(null);
		const posterUrl = useMemo(() => (poster ? URL.createObjectURL(poster) : null), [poster]);
		useEffect(() => () => (posterUrl ? URL.revokeObjectURL(posterUrl) : undefined), [posterUrl]);
		const canPickFrame = decodable && duration !== null && duration > 0;
		// The frame slider's decoder goes when the slider closes, not at the next garbage collection.
		useEffect(() => {
			const video = frameRef.current;
			return () => {
				if (!video) return;
				video.removeAttribute('src');
				video.load();
			};
		}, [choosing]);

		const uploadCover = async () => {
			const [file] = await openFilePicker({accept: FEED_POSTER_ACCEPT});
			if (!file) return;
			const rejection = coverRejectionText(i18n, file);
			if (rejection) {
				ToastCommands.error(rejection);
				return;
			}
			setChoosing(false);
			onChange(file);
		};
		const seek = (seconds: number) => {
			setTime(seconds);
			if (frameRef.current) frameRef.current.currentTime = seconds;
		};
		const takeFrame = async () => {
			const video = frameRef.current;
			if (!video) return;
			setSaving(true);
			const file = await posterFromFrame(video).catch(() => null);
			setSaving(false);
			if (!file) {
				ToastCommands.error(i18n._(FRAME_FAILED_DESCRIPTOR));
				return;
			}
			setChoosing(false);
			onChange(file);
		};

		const hint = !decodable ? COVER_NO_FRAMES_DESCRIPTOR : poster ? COVER_CHOSEN_DESCRIPTOR : COVER_AUTO_DESCRIPTOR;
		return (
			<section className={styles.picker} data-flx="feed.feed-video-poster-picker.picker">
				<div className={styles.summary} data-flx="feed.feed-video-poster-picker.summary">
					<div className={styles.thumb} data-flx="feed.feed-video-poster-picker.thumb">
						{posterUrl ? (
							<img
								src={posterUrl}
								alt=""
								className={styles.thumbImage}
								data-flx="feed.feed-video-poster-picker.image"
							/>
						) : (
							<FilmStripIcon className={styles.thumbIcon} aria-hidden data-flx="feed.feed-video-poster-picker.icon" />
						)}
					</div>
					<div className={styles.text} data-flx="feed.feed-video-poster-picker.text">
						<span className={styles.title} data-flx="feed.feed-video-poster-picker.title">
							{i18n._(COVER_DESCRIPTOR)}
						</span>
						<span className={styles.hint} data-flx="feed.feed-video-poster-picker.hint">
							{i18n._(hint)}
						</span>
					</div>
					{poster && (
						<FocusRing offset={-2} data-flx="feed.feed-video-poster-picker.remove-focus-ring">
							<button
								type="button"
								className={styles.remove}
								onClick={() => onChange(null)}
								disabled={disabled}
								aria-label={i18n._(REMOVE_COVER_DESCRIPTOR)}
								data-flx="feed.feed-video-poster-picker.button.remove"
							>
								<XIcon weight="bold" aria-hidden data-flx="feed.feed-video-poster-picker.x-icon" />
							</button>
						</FocusRing>
					)}
				</div>
				{choosing && canPickFrame ? (
					<div className={styles.frames} data-flx="feed.feed-video-poster-picker.frames">
						<video
							ref={frameRef}
							src={videoUrl}
							muted
							playsInline
							preload="auto"
							className={styles.frameVideo}
							onLoadedData={() => seek(time)}
							data-flx="feed.feed-video-poster-picker.frame-video"
						/>
						<div className={styles.scrub} data-flx="feed.feed-video-poster-picker.scrub">
							<input
								type="range"
								min={0}
								max={duration}
								step={0.1}
								value={time}
								onChange={(event) => seek(Number(event.target.value))}
								disabled={disabled || saving}
								aria-label={i18n._(FRAME_TIME_DESCRIPTOR)}
								aria-valuetext={clock(time)}
								className={styles.slider}
								data-flx="feed.feed-video-poster-picker.slider"
							/>
							<span className={styles.clock} data-flx="feed.feed-video-poster-picker.clock">
								{clock(time)} / {clock(duration)}
							</span>
						</div>
						<div className={styles.frameActions} data-flx="feed.feed-video-poster-picker.frame-actions">
							<Button
								small
								fitContent
								variant="secondary"
								onClick={() => setChoosing(false)}
								disabled={saving}
								data-flx="feed.feed-video-poster-picker.button.cancel"
							>
								{i18n._(CANCEL_FRAME_DESCRIPTOR)}
							</Button>
							<Button
								small
								fitContent
								onClick={takeFrame}
								submitting={saving}
								disabled={disabled}
								data-flx="feed.feed-video-poster-picker.button.use-frame"
							>
								{i18n._(USE_FRAME_DESCRIPTOR)}
							</Button>
						</div>
					</div>
				) : (
					<div className={styles.actions} data-flx="feed.feed-video-poster-picker.actions">
						<FocusRing offset={-2} data-flx="feed.feed-video-poster-picker.upload-focus-ring">
							<button
								type="button"
								className={styles.option}
								onClick={uploadCover}
								disabled={disabled}
								data-flx="feed.feed-video-poster-picker.button.upload"
							>
								<ImageIcon weight="bold" aria-hidden data-flx="feed.feed-video-poster-picker.image-icon" />
								{i18n._(UPLOAD_COVER_DESCRIPTOR)}
							</button>
						</FocusRing>
						{canPickFrame && (
							<FocusRing offset={-2} data-flx="feed.feed-video-poster-picker.frame-focus-ring">
								<button
									type="button"
									className={styles.option}
									onClick={() => setChoosing(true)}
									disabled={disabled}
									data-flx="feed.feed-video-poster-picker.button.pick-frame"
								>
									<FilmStripIcon weight="bold" aria-hidden data-flx="feed.feed-video-poster-picker.film-icon" />
									{i18n._(PICK_FRAME_DESCRIPTOR)}
								</button>
							</FocusRing>
						)}
					</div>
				)}
			</section>
		);
	},
);
